import React, { useEffect, useState } from 'react';
import { Alert, Table } from 'antd';
import { PaginationConfig, SorterResult } from 'antd/lib/table';
import Spin from 'src/components/common/Spin';
import { BugItem } from '../../../types/Bugs';
import { QaError } from '../../../types/QaErrors';
import useBugsListFetch from './hooks/useBugsListFetch';
import useBugsFiltersFetch from './hooks/useBugsFiltersFetch';
import { ColumnId, ColumnSorting, defaultPagination, getColumns } from './BugsTable.config';
import styles from './BugsTable.module.less';

type BugsTableProps = {
  scriptData: QaError;
};

export const DEFAULT_PAGE_SIZE = 10;
export const PAGE_NUMBER = 0;

export const BugsTable: React.FC<BugsTableProps> = ({ scriptData }) => {
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(PAGE_NUMBER);
  const [sorting, setSorting] = useState<ColumnSorting>({ field: 'bugId', order: 'ascend' });
  const { filters, currentFilters, changeCurrentFilters } = useBugsFiltersFetch({
    siteCode: scriptData.siteCode,
    appName: scriptData.applicationName,
  });
  const [{ data, error, total }] = useBugsListFetch({
    siteCode: scriptData.siteCode,
    appName: scriptData.applicationName,
    filters: currentFilters,
    sorting,
    page,
    pageSize,
  });
  const [firstLoading, setFirstLoading] = useState(true);
  useEffect(() => {
    setTimeout(() => {
      setFirstLoading(false);
    }, 5000);
  }, []);
  const totalRender = () => <span>Total Results: {total}</span>;
  const emptyText = <p className={styles.textNoResults}>No results found</p>;

  const handlePageSizeChanges = (_: number, size: number): void => {
    setPageSize(size);
    setPage(0);
  };

  const handleTableChange = (
    _: unused,
    newFilters: Partial<Record<ColumnId, string[]>>,
    sorter: SorterResult<BugItem>
  ) => {
    const order =
      sorting.field === sorter.columnKey && sorter.order === undefined
        ? sorting.order === 'ascend'
          ? 'descend'
          : 'ascend'
        : sorter.order;

    const bugStatus =
      newFilters?.bugStatus?.join(',') ||
      filters?.statuses
        ?.map((item) => item.value)
        ?.filter((status) => status !== 'Closed' && status !== 'Resolved')
        .join(',');
    const bugPriority = newFilters?.bugPriority?.join(',');
    const organization = newFilters?.organization?.join(',');
    const newCurrentFilters = {
      bugStatus: bugStatus,
      bugPriority: bugPriority,
      organization: organization,
    };
    changeCurrentFilters({ ...newCurrentFilters });
    setSorting({ field: sorter.columnKey as ColumnId, order: order });
  };
  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const pagination: PaginationConfig = {
    ...defaultPagination,
    pageSize: pageSize,
    current: page,
    total,
    showTotal: totalRender,
    onShowSizeChange: handlePageSizeChanges,
    onChange: handlePageChange,
  };

  return (
    <>
      {error && (
        <Alert message="Error" description="Something went wrong. Please try again later..." type="error" showIcon />
      )}
      <Spin spinning={firstLoading}>
        <Table
          className={styles.listTable}
          rowKey={(_, index) => String(index)}
          columns={getColumns(sorting, filters, currentFilters)}
          pagination={pagination}
          dataSource={data || []}
          onChange={handleTableChange}
          tableLayout={'auto'}
          locale={{ emptyText }}
        />
      </Spin>
    </>
  );
};
